// Step 1: the SK model. A saved fit of one of your SKFitter projects (rebuilt on the server into <seed>_model.json),
// or a model file saved earlier (by the desktop program, a script, or another project).
import { Alert, Badge, Button, FileButton, Group, Loader, Select, Stack, Table, Tabs, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { JobProgress, JobStatusBadge } from "../../components/Jobs";
import { formatDate, get, post, uploadFile, type Job, type Limits, type ProjectDetail } from "../../lib/api";
import { useLiveJob } from "../../lib/hooks";
import { type ModelSummary, type NegfFit, type NegfMemory } from "../../lib/negf";
import { describeModifiers } from "../../lib/fields";
import { ModelTools } from "./ModelTools";
import { WannierPanel } from "./WannierPanel";
import { ContinuumPanel } from "./ContinuumPanel";
import { TextbookPanel } from "../properties/TextbookPanel";
import { wannierRuns, type WannierRunItem } from "../../lib/wannier";

export function fitLabel(f: NegfFit): string {
  const rmse = f.rmse_meV != null ? `, RMSE ${f.rmse_meV.toFixed(1)} meV` : "";
  return `${f.project_name} · ${f.seed}${rmse}${f.current ? "" : ` (${formatDate(f.created_at)})`}`;
}

export function ModelSummaryTable({ s }: { s: ModelSummary }) {
  const flags = [s.spinful ? (s.soc ? "spin–orbit" : "collinear magnet") : "spin-degenerate", s.non_orthogonal ? "non-orthogonal" : "orthogonal"];
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text fw={600}>{s.formula}</Text>
        {s.kind === "wannier" && <Badge variant="filled" color="grape">Wannier90</Badge>}
        {flags.map((f) => <Badge key={f} variant="light">{f}</Badge>)}
        {s.kind === "wannier"
          ? <Badge variant="outline">{s.n_hoppings ?? 0} hopping blocks</Badge>
          : <Badge variant="outline">{s.n_neighbors} neighbour shells</Badge>}
      </Group>
      <Table withTableBorder fz="sm" data-testid="model-summary">
        <Table.Tbody>
          <Table.Tr><Table.Td>Atoms in the cell</Table.Td><Table.Td>{s.n_sites} ({s.species.join(", ")})</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Orbitals per cell (with spin)</Table.Td><Table.Td>{s.orbitals_per_cell}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Orbitals per atom</Table.Td>
            <Table.Td>{s.site_orbitals.map((o, i) => `${s.species[i]}: ${o.join(" ")}`).join(" · ")}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Cell vectors |a1|, |a2|, |a3|</Table.Td><Table.Td>{s.lengths.map((l) => l.toFixed(3)).join(", ")} Å</Table.Td></Table.Tr>
          {s.reach && <Table.Tr><Table.Td>Longest hopping (cells along a1, a2, a3)</Table.Td><Table.Td>{s.reach.join(", ")}</Table.Td></Table.Tr>}
          {!!s.modifiers?.length && <Table.Tr><Table.Td>Changes to the model</Table.Td>
            <Table.Td data-testid="model-modifiers">{describeModifiers(s.modifiers).join(" · ")}</Table.Td></Table.Tr>}
        </Table.Tbody>
      </Table>
      {(s.warnings ?? []).map((w) => <Alert key={w} color="yellow" p="xs" data-testid="model-warning"><Text size="xs">{w}</Text></Alert>)}
      {s.non_orthogonal && <Text size="xs" c="dimmed">Non-orthogonal fit: the overlap S is used everywhere - transmission,
        densities and LDOS as Mulliken populations, self-consistent potentials and Hubbard U.</Text>}
    </Stack>
  );
}

/** The model step of NEGF Transport, also used by Properties (``product``: whose jobs build and change the model). */
export function ModelStep({ project, mem, remember, update, onNext, limits, product = "negf", nextLabel = "Next: device" }: {
  project: ProjectDetail; mem: NegfMemory; remember: (m: Partial<NegfMemory>) => void; update?: (m: Partial<NegfMemory>) => void;
  onNext: () => void; limits?: Limits; product?: "negf" | "properties"; nextLabel?: string;
}) {
  const [tab, setTab] = useState<string>(mem.source?.kind === "wannier" ? (mem.wannier_run ? "wannier_run" : "wannier")
    : mem.source?.kind === "upload" ? "file"
    : mem.source?.kind === "continuum" && product === "negf" ? "continuum"
    : mem.source?.kind === "textbook" && product === "properties" ? "textbook" : "fit");
  const qc = useQueryClient();
  const fits = useQuery({ queryKey: [`${product}-fits`], queryFn: () => get<NegfFit[]>(`/${product}/fits`) });
  const [choice, setChoice] = useState<string | null>(mem.source?.kind === "fit" ? mem.source.fit_job : null);
  useEffect(() => { if (!choice && fits.data?.length) setChoice(fits.data[0].fit_job); }, [choice, fits.data]);
  const { job } = useLiveJob(mem.model_job);

  // a finished model job: remember its model file and summary
  useEffect(() => {
    if (job?.status === "succeeded" && job.result?.model_file && job.result.model_file !== mem.model_file
        && job.result.model_file !== mem.base_file && !mem.base_file)
      remember({ model_file: job.result.model_file, summary: job.result.summary as ModelSummary });
  }, [job, mem.model_file, mem.base_file, remember]);

  const started = (j: Job, source: NegfMemory["source"]) => {
    remember({ model_job: j.id, source, wannier_run: undefined, model_file: undefined, summary: undefined, base_file: undefined, base_summary: undefined,
      modify_job: undefined, edges_jobs: undefined });
    qc.invalidateQueries({ queryKey: ["project", project.id] });
    qc.invalidateQueries({ queryKey: ["me"] });
  };
  const fromFit = useMutation({
    mutationFn: async () => {
      const f = fits.data?.find((x) => x.fit_job === choice);
      if (!f) throw new Error("Choose a fit first.");
      const j = await post<Job>("/jobs", { kind: `${product}.model`, project_id: project.id, params: f.model_params });
      return { j, f };
    },
    onSuccess: ({ j, f }) => started(j, { kind: "fit", fit_job: f.fit_job, project_name: f.project_name, seed: f.seed }),
    onError: (e) => notifyError(e),
  });
  const fromFile = useMutation({
    mutationFn: async (file: File) => {
      const stored = await uploadFile(project.id, file);
      return { j: await post<Job>("/jobs", { kind: `${product}.model`, project_id: project.id, params: { model_file: stored.id } }), name: file.name };
    },
    onSuccess: ({ j, name }) => started(j, { kind: "upload", name }),
    onError: (e) => notifyError(e),
  });

  return (
    <Stack gap="md" mt="md">
      <Tabs value={tab} onChange={(v) => v && setTab(v)}>
        <Tabs.List>
          <Tabs.Tab value="fit">Slater-Koster fit</Tabs.Tab>
          <Tabs.Tab value="wannier_run">Wannier run</Tabs.Tab>
          <Tabs.Tab value="wannier">Wannier90 files</Tabs.Tab>
          <Tabs.Tab value="file">Model file</Tabs.Tab>
          {product === "negf" && <Tabs.Tab value="continuum">Continuum wire</Tabs.Tab>}
          {product === "properties" && <Tabs.Tab value="textbook">Textbook model</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel value="fit" pt="sm">
          <Stack gap="xs">
            {fits.isLoading ? <Loader size="sm" /> : fits.error ? <ErrorBox error={fits.error} /> : !fits.data?.length ? (
              <Alert color="blue">No saved fits yet. Make a fit in a Tight Binding project first, or use Wannier90
                files or a model file.</Alert>
            ) : (
              <>
                <Select label="Saved fit" data={fits.data.map((f) => ({ value: f.fit_job, label: fitLabel(f) }))} value={choice}
                  onChange={setChoice} allowDeselect={false} searchable data-testid="fit-select" />
                <Group><Button onClick={() => fromFit.mutate()} loading={fromFit.isPending} disabled={!choice}>Use this fit</Button></Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="wannier_run" pt="sm">
          <WannierRunPanel project={project} product={product} started={(j, source, run) => { started(j, source); remember({ wannier_run: run }); }} />
        </Tabs.Panel>
        <Tabs.Panel value="wannier" pt="sm">
          <WannierPanel project={project} mem={mem} remember={remember} update={update} started={started} product={product} />
        </Tabs.Panel>
        <Tabs.Panel value="file" pt="sm">
          <Stack gap="xs">
            <Text size="sm" c="dimmed">A model saved as <code>&lt;seed&gt;_model.json</code> (a Slater-Koster or Wannier90 model from the desktop
              program, scripts, or an earlier project).</Text>
            <Group>
              <FileButton onChange={(f) => f && fromFile.mutate(f)} accept=".json,application/json">
                {(props) => <Button variant="default" loading={fromFile.isPending} {...props}>Upload model file</Button>}
              </FileButton>
            </Group>
          </Stack>
        </Tabs.Panel>
        {product === "negf" && <Tabs.Panel value="continuum" pt="sm">
          <ContinuumPanel project={project} mem={mem} remember={remember} update={update} started={started} />
        </Tabs.Panel>}
        {product === "properties" && <Tabs.Panel value="textbook" pt="sm">
          <TextbookPanel project={project} mem={mem} started={started} />
        </Tabs.Panel>}
      </Tabs>

      {job && job.status !== "succeeded" && (
        <Stack gap={4}>
          <Group gap="xs"><JobStatusBadge status={job.status} /><Text size="sm">Building the model</Text></Group>
          <JobProgress job={job} />
          {job.error && <ErrorBox error={new Error(job.error)} />}
        </Stack>
      )}
      {mem.summary && (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">Model: {mem.source?.kind === "fit" ? `fit "${mem.source.seed}" of ${mem.source.project_name}`
            : mem.source?.kind === "upload" ? mem.source.name
            : mem.source?.kind === "wannier" ? `Wannier90 "${mem.source.name}" (${mem.source.spin === "none" ? "no spin" : mem.source.spin})`
            : mem.source?.kind === "example" ? `the illustrative project "${mem.source.name}"`
            : mem.source?.kind === "continuum" ? `the continuum wire "${mem.source.name}"`
            : mem.source?.kind === "textbook" ? `the textbook ${mem.source.title}${Object.keys(mem.source.params).length
              ? ` (${Object.entries(mem.source.params).map(([k, v]) => `${k} = ${v}`).join(", ")})` : ""}`
            : "saved model"}</Text>
          <ModelSummaryTable s={mem.summary} />
          <Group justify="flex-end"><Button onClick={onNext}>{nextLabel}</Button></Group>
          {mem.model_file && limits && <ModelTools project={project} mem={mem} remember={remember} update={update} limits={limits} product={product} />}
        </Stack>
      )}
    </Stack>
  );
}

const runLabel = (r: WannierRunItem) => {
  const om = r.omega ? Object.values(r.omega)[0] : null;
  return `${r.project_name ?? "Wannier"} · ${r.seed ?? "wannier"} · ${r.num_wann ?? "?"} functions${om != null ? `, Ω ${om.toFixed(2)} Å²` : ""}${r.created_at ? ` (${formatDate(r.created_at)})` : ""}`;
};

/** "Wannier run": the model of one of your finished Wannier runs (Tight Binding → Wannier functions), copied here. */
export function WannierRunPanel({ project, product, started }: {
  project: ProjectDetail; product: "negf" | "properties"; started: (j: Job, source: NegfMemory["source"], run: string) => void;
}) {
  const runs = useQuery({ queryKey: ["wannier-runs"], queryFn: wannierRuns, retry: false });
  const [choice, setChoice] = useState<string | null>(null);
  const list = runs.data ?? [];
  const chosen = list.find((r) => r.job_id === choice) ?? list[0];
  const use = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: `${product}.model`, project_id: project.id,
      params: { model_file: chosen!.model_file, copy: true, wannier_run: chosen!.job_id } }),
    onSuccess: (j) => started(j, { kind: "wannier", name: chosen!.seed ?? "wannier", spin: (chosen!.spin ?? "none") as "none" }, chosen!.job_id),
    onError: (e) => notifyError(e),
  });
  if (runs.isLoading) return <Loader size="sm" />;
  if (runs.error) return <Alert color="gray">Wannier functions are not available on your plan.</Alert>;
  if (!list.length)
    return <Alert color="blue" data-testid="no-wannier-runs">No Wannier runs yet. Make one in a Wannier functions project (Tight Binding),
      or use Wannier90 files.</Alert>;
  return (
    <Stack gap="xs" data-testid="wannier-run-panel">
      <Select label="Wannier run" data={list.map((r) => ({ value: r.job_id, label: runLabel(r) }))} value={chosen?.job_id ?? null}
        onChange={setChoice} allowDeselect={false} searchable />
      {chosen && (chosen.warnings > 0 || chosen.band_error_max != null) && (
        <Text size="xs" c="dimmed">{chosen.band_error_max != null ? `Band error ${(chosen.band_error_max * 1000).toFixed(1)} meV` : ""}
          {chosen.warnings > 0 ? ` · ${chosen.warnings} warning${chosen.warnings > 1 ? "s" : ""} in its quality report` : ""}</Text>
      )}
      <Text size="xs" c="dimmed">The model keeps every hopping; for short leads, use a model made again with a cut-off (Results step of the Wannier project).</Text>
      <Group><Button onClick={() => use.mutate()} loading={use.isPending} disabled={!chosen?.model_file}>Use this model</Button></Group>
    </Stack>
  );
}
